import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  hasGuestFlowAuth,
  readGuestFlowAuth,
  startGuestEntrySession,
  clearGuestFlowAuth,
} from './guestFlowAuth';
import { startFranchiseGeofenceMonitor } from './franchiseGeofence';
import CameraPrepPlaceholder from './CameraPrepPlaceholder';

/**
 * Guards guest mobile Face Identity routes (QR scan flow).
 * @param {{ children: import('react').ReactNode, entryPath?: string }} props
 */
export default function RequireGuestFlowAuth({ children, entryPath = '/nyafaz-ep/passport/entry' }) {
  const location = useLocation();
  const auth = readGuestFlowAuth();
  const state = location.state || {};
  const bootstrapRef = String(state.franchiseGuestRef || state.franchise_code || '').trim();
  const shouldBootstrap = Boolean(state.bootstrapGuestEntry && bootstrapRef && !hasGuestFlowAuth());
  const [bootstrapError, setBootstrapError] = useState('');
  const [bootstrapDone, setBootstrapDone] = useState(() => hasGuestFlowAuth() || !shouldBootstrap);

  useEffect(() => {
    if (!shouldBootstrap || hasGuestFlowAuth()) {
      setBootstrapDone(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await startGuestEntrySession(bootstrapRef);
        if (!cancelled) {
          setBootstrapDone(true);
          setBootstrapError('');
        }
      } catch (err) {
        if (!cancelled) {
          setBootstrapError(err?.message || 'Could not start guest check-in');
          setBootstrapDone(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldBootstrap, bootstrapRef]);

  useEffect(() => {
    if (!hasGuestFlowAuth()) return undefined;
    const stop = startFranchiseGeofenceMonitor(
      () => readGuestFlowAuth(),
      () => {
        const current = readGuestFlowAuth();
        clearGuestFlowAuth();
        const code = current?.franchise_code;
        const target = code
          ? `${entryPath}?franchise_code=${encodeURIComponent(code)}`
          : entryPath;
        window.location.replace(target);
      },
    );
    return () => stop();
  }, [auth?.franchise_id, auth?.signed_in_at, entryPath]);

  if (shouldBootstrap && !bootstrapDone) {
    return <CameraPrepPlaceholder message="Opening check-in…" />;
  }

  if (!hasGuestFlowAuth()) {
    if (bootstrapError) {
      const params = new URLSearchParams(location.search || '');
      const code = params.get('franchise_code') || params.get('franchiseCode') || bootstrapRef || '';
      const query = code ? `franchise_code=${encodeURIComponent(code)}` : '';
      const target = query ? `${entryPath}?${query}` : entryPath;
      return (
        <Navigate
          to={target}
          replace
          state={{ from: `${location.pathname}${location.search || ''}`, error: bootstrapError }}
        />
      );
    }
    const params = new URLSearchParams(location.search || '');
    const code = params.get('franchise_code') || params.get('franchiseCode') || '';
    const fid = params.get('franchise_id') || params.get('franchiseId') || '';
    const query = code
      ? `franchise_code=${encodeURIComponent(code)}`
      : fid
        ? `franchise_id=${encodeURIComponent(fid)}`
        : '';
    const target = query ? `${entryPath}?${query}` : entryPath;
    return <Navigate to={target} replace state={{ from: `${location.pathname}${location.search || ''}` }} />;
  }

  return children;
}
